import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Icon from '../Icon';
import api, { formatApiErrorDetail } from '../../lib/api';
import { panelCls, primaryBtn, ghostBtn, SectionHead, Note } from './ui';
import MedicationForm, { FREQUENCIES } from './MedicationForm';

const freqLabel = (v) => (FREQUENCIES.find((f) => f.v === v) || {}).l || v;
const todayIso = () => new Date().toISOString().slice(0, 10);

const GROUPS = [
  { key: 'current', title: 'Current medications', statuses: ['ongoing'] },
  { key: 'paused', title: 'Paused', statuses: ['paused'] },
  { key: 'closed', title: 'Completed / discontinued', statuses: ['completed', 'discontinued'] },
];

const nowHHMM = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

export default function MedicationsPanel() {
  const [meds, setMeds] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [safetyNote, setSafetyNote] = useState('');
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState('');
  const [duplicate, setDuplicate] = useState(null); // {payload, existing, message}
  const [removing, setRemoving] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [toast, setToast] = useState('');
  const [note, setNote] = useState('');
  const [history, setHistory] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [snoozedLocal, setSnoozedLocal] = useState({});

  const load = useCallback(async () => {
    const [list, sched] = await Promise.all([
      api.get('/me/medications'),
      api.get('/me/medications/schedule'),
    ]);
    setMeds(list.data.medications || []);
    setSafetyNote(list.data.safety_note || '');
    setSchedule(sched.data.doses || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  const flash = (msg, extra) => {
    setToast(msg);
    setNote(extra || '');
    setTimeout(() => setToast(''), 4000);
  };

  const save = async (payload, confirmDuplicate = false) => {
    setBusy(true);
    setFormError('');
    try {
      if (editing) {
        await api.put(`/me/medications/${editing.id}`, payload);
        flash('Medication details updated.');
      } else {
        await api.post(`/me/medications${confirmDuplicate ? '?confirm_duplicate=true' : ''}`, payload);
        flash('Medication added successfully.');
      }
      setAdding(false);
      setEditing(null);
      setDuplicate(null);
      await load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.code === 'possible_duplicate') {
        setDuplicate({ payload, existing: detail.existing, message: detail.message });
      } else {
        setFormError(formatApiErrorDetail(detail) || 'We couldn’t save this medication. Please check the information and try again.');
      }
    } finally {
      setBusy(false);
    }
  };

  const remove = async (med) => {
    setBusy(true);
    try {
      await api.delete(`/me/medications/${med.id}`);
      setRemoving(null);
      flash('Medication removed.');
      await load();
    } finally {
      setBusy(false);
    }
  };

  const recordDose = async (dose, status) => {
    const { data } = await api.post(`/me/medications/${dose.medication_id}/doses`, {
      scheduled_date: dose.scheduled_date,
      scheduled_time: dose.scheduled_time,
      status,
    });
    flash(data.message, data.note);
    await load();
    if (showHistory) loadHistory();
  };

  const snooze = async (dose) => {
    const { data } = await api.post(`/me/medications/${dose.medication_id}/snooze`, {
      scheduled_date: dose.scheduled_date,
      scheduled_time: dose.scheduled_time,
      minutes: dose.snooze_minutes || 10,
    });
    setSnoozedLocal((s) => ({ ...s, [`${dose.medication_id}-${dose.scheduled_time}`]: data.snoozed_until }));
    flash(data.message);
    await load();
  };

  const loadHistory = async () => {
    const { data } = await api.get('/me/medications/history?days=30');
    setHistory(data);
  };

  const toggleHistory = async () => {
    const next = !showHistory;
    setShowHistory(next);
    if (next && !history) await loadHistory();
  };

  // Doses that are due now (scheduled time has passed, nothing recorded, not snoozed).
  const dueNow = useMemo(() => {
    const t = nowHHMM();
    return schedule.filter((d) => {
      if (d.status === 'taken' || d.status === 'skipped' || d.status === 'missed') return false;
      if (d.notification_preference === 'off' || d.reminders_enabled === false) return false;
      const until = d.snoozed_until || snoozedLocal[`${d.medication_id}-${d.scheduled_time}`];
      if (until && new Date(until) > new Date()) return false;
      if (!d.scheduled_time) return false;
      return d.scheduled_time <= t;
    });
  }, [schedule, snoozedLocal]);

  // Browser notifications, only for medications where the user asked for them.
  // Already-notified doses are tracked in memory only: nothing about a
  // medication is ever written to browser storage.
  const notifiedRef = useRef(new Set());
  useEffect(() => {
    const wantsBrowser = dueNow.some((d) => d.notification_preference === 'browser');
    if (!wantsBrowser || typeof Notification === 'undefined') return;
    if (Notification.permission === 'default') Notification.requestPermission();
    if (Notification.permission !== 'granted') return;
    dueNow
      .filter((d) => d.notification_preference === 'browser')
      .forEach((d) => {
        const key = `medrem-${d.medication_id}-${d.scheduled_date}-${d.scheduled_time}`;
        if (notifiedRef.current.has(key)) return;
        notifiedRef.current.add(key);
        new Notification('Medication reminder', {
          body: `${d.name} — ${d.dosage}\nScheduled for ${d.scheduled_time}`,
          tag: key,
        });
      });
  }, [dueNow]);

  const grouped = GROUPS.map((g) => ({ ...g, items: meds.filter((m) => g.statuses.includes(m.status)) }));
  const nextDoseFor = (medId) => {
    const t = nowHHMM();
    const items = schedule.filter((d) => d.medication_id === medId && d.scheduled_time);
    const upcoming = items.find((d) => d.scheduled_time >= t && !d.status);
    return upcoming ? `Today at ${upcoming.scheduled_time}` : items.length ? 'No more doses today' : 'No times set';
  };

  return (
    <div className={`${panelCls} ph-no-capture`} data-testid="medications-panel">
      <SectionHead eyebrow="Organised In One Place" title="My Medications">
        <div className="flex flex-wrap items-center gap-space-sm">
          <button type="button" data-testid="med-history-toggle" onClick={toggleHistory} className={ghostBtn}>
            <Icon name="history" className="text-[18px]" />
            <span>{showHistory ? 'Hide history' : 'View history'}</span>
          </button>
          {!adding && !editing && (
            <button type="button" data-testid="med-add-button" onClick={() => { setAdding(true); setFormError(''); }} className={primaryBtn}>
              <Icon name="add" className="text-[18px]" />
              <span>Add medication</span>
            </button>
          )}
        </div>
      </SectionHead>

      {toast ? (
        <div data-testid="med-toast" className="mb-space-md px-space-md py-space-sm rounded-lg bg-primary-container text-outline font-body-sm text-body-sm">
          {toast}{note ? <span className="block text-on-surface-variant mt-1">{note}</span> : null}
        </div>
      ) : null}

      {/* Reminders due now */}
      {dueNow.length > 0 && (
        <div className="mb-space-lg flex flex-col gap-space-sm" data-testid="med-reminders">
          {dueNow.map((d) => (
            <div key={`${d.medication_id}-${d.scheduled_time}`} data-testid={`med-reminder-${d.medication_id}`} className="p-space-md rounded-xl bg-outline text-surface flex flex-col sm:flex-row sm:items-center justify-between gap-space-md">
              <div className="flex items-start gap-space-md">
                <span className="w-10 h-10 rounded-full bg-surface-tint text-on-primary flex items-center justify-center shrink-0"><Icon name="notifications_active" className="text-[20px]" /></span>
                <div>
                  <span className="font-label-sm text-label-sm uppercase tracking-widest text-primary-fixed block">Medication reminder</span>
                  <p className="font-title-md text-title-md text-surface font-bold">{d.name} — {d.dosage}</p>
                  <p className="font-body-sm text-body-sm text-surface-container-high">Scheduled for {d.scheduled_time}{d.timing ? ` • ${d.timing}` : ''}</p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-space-xs">
                <button type="button" data-testid={`med-reminder-taken-${d.medication_id}`} onClick={() => recordDose(d, 'taken')} className="h-10 px-space-md rounded-lg bg-surface-tint text-on-primary font-label-md text-label-md uppercase tracking-wider">Taken</button>
                <button type="button" data-testid={`med-reminder-snooze-${d.medication_id}`} onClick={() => snooze(d)} className="h-10 px-space-md rounded-lg bg-surface/15 text-surface font-label-md text-label-md uppercase tracking-wider">Snooze</button>
                <button type="button" data-testid={`med-reminder-skip-${d.medication_id}`} onClick={() => recordDose(d, 'skipped')} className="h-10 px-space-md rounded-lg bg-surface/15 text-surface font-label-md text-label-md uppercase tracking-wider">Skip</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add / edit form */}
      {(adding || editing) && (
        <div className="mb-space-lg">
          <MedicationForm
            initial={editing}
            busy={busy}
            error={formError}
            onCancel={() => { setAdding(false); setEditing(null); setFormError(''); }}
            onSave={(payload) => save(payload)}
          />
        </div>
      )}

      {/* Possible duplicate confirmation */}
      {duplicate && (
        <div data-testid="med-duplicate-dialog" className="mb-space-lg p-space-lg rounded-xl bg-primary-container border-l-4 border-surface-tint">
          <p className="font-title-md text-title-md text-outline mb-space-xs">{duplicate.message}</p>
          <p className="font-body-sm text-body-sm text-on-surface-variant mb-space-md">
            You already have {duplicate.existing?.name} ({duplicate.existing?.dosage}, {freqLabel(duplicate.existing?.frequency)}).
            We won’t merge them — it’s your choice.
          </p>
          <div className="flex flex-wrap items-center gap-space-sm">
            <button type="button" data-testid="med-duplicate-confirm" onClick={() => save(duplicate.payload, true)} className={primaryBtn}>Add it separately</button>
            <button type="button" data-testid="med-duplicate-cancel" onClick={() => setDuplicate(null)} className={ghostBtn}>Go back</button>
          </div>
        </div>
      )}

      {/* Today's doses */}
      {schedule.length > 0 && (
        <div className="mb-space-xl" data-testid="med-today">
          <h4 className="font-title-md text-title-md uppercase tracking-wider text-outline mb-space-sm">Today ({todayIso()})</h4>
          <div className="flex flex-col gap-space-sm">
            {schedule.map((d) => (
              <div key={`${d.medication_id}-${d.scheduled_time}`} data-testid={`med-dose-${d.medication_id}-${d.scheduled_time || 'any'}`} className="p-space-md rounded-lg bg-surface border border-surface-container flex flex-col sm:flex-row sm:items-center justify-between gap-space-sm">
                <div className="flex items-center gap-space-md">
                  <span className="font-label-md text-label-md uppercase tracking-wider text-surface-tint font-bold w-16">{d.scheduled_time || '—'}</span>
                  <div>
                    <p className="font-title-md text-body-md font-semibold text-outline">{d.name} — {d.dosage}</p>
                    <p className="font-body-sm text-[12px] text-on-surface-variant">{d.timing || freqLabel(d.frequency) || 'As entered'}{d.instructions ? ` • ${d.instructions}` : ''}</p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-space-xs">
                  {d.status && d.status !== 'snoozed' ? (
                    <span data-testid={`med-dose-status-${d.medication_id}`} className="px-space-md h-9 inline-flex items-center rounded-full bg-primary-container text-outline font-label-sm text-label-sm uppercase tracking-wider font-bold">
                      <Icon name={d.status === 'taken' ? 'check' : 'remove'} className="text-[14px] mr-1" />
                      Recorded as {d.status}
                    </span>
                  ) : (
                    <>
                      <button type="button" data-testid={`dose-taken-${d.medication_id}`} onClick={() => recordDose(d, 'taken')} className="h-9 px-space-md rounded bg-surface-tint text-on-primary font-label-sm text-label-sm uppercase tracking-wider">Taken</button>
                      <button type="button" data-testid={`dose-skipped-${d.medication_id}`} onClick={() => recordDose(d, 'skipped')} className="h-9 px-space-md rounded bg-surface-container text-outline font-label-sm text-label-sm uppercase tracking-wider">Skipped</button>
                      <button type="button" data-testid={`dose-missed-${d.medication_id}`} onClick={() => recordDose(d, 'missed')} className="h-9 px-space-md rounded bg-surface-container text-outline font-label-sm text-label-sm uppercase tracking-wider">Missed</button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {meds.length === 0 && !adding ? (
        <div className="p-space-xl rounded-xl bg-surface-container-low border border-surface-container text-center" data-testid="med-empty">
          <h4 className="font-headline-sm text-headline-sm text-outline uppercase mb-space-xs">My Medications</h4>
          <p className="font-body-md text-body-md text-on-surface-variant mb-space-lg">Keep your current medications organized in one place.</p>
          <button type="button" data-testid="med-empty-add" onClick={() => setAdding(true)} className={primaryBtn}>
            <Icon name="add" className="text-[18px]" /><span>Add medication</span>
          </button>
        </div>
      ) : null}

      {/* Grouped lists */}
      {grouped.map((g) => (
        g.items.length > 0 ? (
          <div key={g.key} className="mb-space-lg" data-testid={`med-group-${g.key}`}>
            <h4 className="font-title-md text-title-md uppercase tracking-wider text-outline mb-space-sm">{g.title}</h4>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-space-md">
              {g.items.map((m) => (
                <div key={m.id} data-testid={`med-card-${m.id}`} className="p-space-md rounded-xl bg-surface border border-surface-container flex flex-col">
                  <div className="flex items-start justify-between gap-space-sm">
                    <div className="min-w-0">
                      <p className="font-headline-sm text-body-lg font-bold text-outline leading-snug">{m.name}</p>
                      <p className="font-body-sm text-body-sm text-on-surface-variant">{m.dosage} • {freqLabel(m.frequency)}</p>
                    </div>
                    <span className="shrink-0 px-space-sm py-1 rounded-full bg-primary-container text-outline font-label-sm text-label-sm uppercase tracking-wider font-bold">{m.status}</span>
                  </div>
                  <p className="font-body-sm text-[12px] text-on-surface-variant mt-space-xs">
                    <Icon name="schedule" className="text-[14px] align-middle mr-1" />
                    {m.status === 'ongoing' ? nextDoseFor(m.id) : 'Not scheduled'}
                  </p>

                  {expanded[m.id] && (
                    <dl className="mt-space-md pt-space-md border-t border-surface-container grid grid-cols-2 gap-space-sm" data-testid={`med-details-${m.id}`}>
                      {[
                        ['Form', m.form],
                        ['Timing', m.timing],
                        ['Started', m.start_date],
                        ['Ends', m.end_date],
                        ['Prescribed by', m.prescriber],
                        ['What it’s for', m.reason],
                        ['Instructions', m.instructions],
                        ['Reminder times', (m.reminder_times || []).join(', ')],
                        ['Notes', m.notes],
                      ].filter(([, v]) => v).map(([k, v]) => (
                        <div key={k}>
                          <dt className="font-label-sm text-label-sm uppercase tracking-wider text-on-surface-variant">{k}</dt>
                          <dd className="font-body-sm text-body-sm text-outline">{v}</dd>
                        </div>
                      ))}
                    </dl>
                  )}

                  <div className="flex flex-wrap items-center gap-space-sm mt-space-md pt-space-md border-t border-surface-container">
                    <button type="button" data-testid={`med-expand-${m.id}`} onClick={() => setExpanded((e) => ({ ...e, [m.id]: !e[m.id] }))} className="font-label-md text-label-md uppercase tracking-wider text-outline hover:text-surface-tint transition-colors">
                      {expanded[m.id] ? 'Hide details' : 'View details'}
                    </button>
                    <button type="button" data-testid={`med-edit-${m.id}`} onClick={() => { setEditing(m); setAdding(false); setFormError(''); }} className="font-label-md text-label-md uppercase tracking-wider text-outline hover:text-surface-tint transition-colors">Edit</button>
                    <button type="button" data-testid={`med-remove-${m.id}`} onClick={() => setRemoving(m)} className="font-label-md text-label-md uppercase tracking-wider text-on-surface-variant hover:text-error transition-colors">Remove</button>
                  </div>

                  {removing && removing.id === m.id && (
                    <div data-testid={`med-remove-confirm-${m.id}`} className="mt-space-md p-space-md rounded-lg bg-error-container">
                      <p className="font-title-md text-body-md text-on-error-container font-bold mb-space-xs">Remove this medication?</p>
                      <p className="font-body-sm text-body-sm text-on-error-container mb-space-md">
                        Removing it will remove it from your medication list. Your previous tracking history may also be affected.
                      </p>
                      <div className="flex items-center gap-space-sm">
                        <button type="button" data-testid={`med-remove-yes-${m.id}`} onClick={() => remove(m)} className="h-9 px-space-md rounded bg-error text-on-error font-label-sm text-label-sm uppercase tracking-wider">Remove</button>
                        <button type="button" data-testid={`med-remove-no-${m.id}`} onClick={() => setRemoving(null)} className="h-9 px-space-md rounded bg-surface text-outline font-label-sm text-label-sm uppercase tracking-wider">Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : null
      ))}

      {/* History */}
      {showHistory && (
        <div className="mb-space-lg" data-testid="med-history">
          <h4 className="font-title-md text-title-md uppercase tracking-wider text-outline mb-space-sm">Last 30 days</h4>
          {history ? (
            <>
              <div className="grid grid-cols-3 gap-space-sm mb-space-md">
                {[['Doses recorded as taken', history.counts.taken], ['Doses recorded as skipped', history.counts.skipped], ['Doses recorded as missed', history.counts.missed]].map(([l, v]) => (
                  <div key={l} className="p-space-md rounded-lg bg-surface border border-surface-container">
                    <span className="font-label-sm text-label-sm uppercase tracking-wider text-on-surface-variant block leading-snug">{l}</span>
                    <p className="font-headline-sm text-title-md font-bold text-outline mt-1">{v}</p>
                  </div>
                ))}
              </div>
              {history.entries.length === 0 ? (
                <p className="font-body-md text-body-md text-on-surface-variant">Nothing recorded yet.</p>
              ) : (
                <ul className="flex flex-col gap-space-xs">
                  {history.entries.slice(0, 40).map((e) => (
                    <li key={e.id} className="flex items-center justify-between p-space-sm rounded bg-surface border border-surface-container font-body-sm text-body-sm">
                      <span className="text-outline">{e.scheduled_date}{e.scheduled_time ? ` • ${e.scheduled_time}` : ''}</span>
                      <span className="text-on-surface-variant">{e.name} {e.dosage ? `— ${e.dosage}` : ''}</span>
                      <span className="font-label-sm text-label-sm uppercase tracking-wider text-outline font-bold">{e.status}</span>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : <p className="font-body-md text-body-md text-on-surface-variant">Loading…</p>}
        </div>
      )}

      <Note testId="med-safety-note">{safetyNote}</Note>
    </div>
  );
}
